// JournalVersionsPanel — thin wrapper over the shared VersioningPanel.
//
// Journal-domain config only. The only domain-specific knowledge that
// lives here is:
//   • The frozen-field rule (already enforced server-side)
//   • The financial fields list (debit/credit/total_*)
//   • Client-side `validate()` mirroring the server's debit==credit
//     guard so the user gets an immediate error instead of a 422.
//   • Amendable fields exclude the line array — line editing is too
//     complex for the generic modal; for line edits the operator falls
//     back to creating a new journal entry. The next phase will add a
//     dedicated lines editor.
import React from 'react';
import VersioningPanel from './versioning/VersioningPanel';

function sumLines(lines) {
  let d = 0;
  let c = 0;
  for (const line of lines || []) {
    d += Number(line.debit || 0);
    c += Number(line.credit || 0);
  }
  return { debit: d, credit: c };
}

const JOURNAL_CONFIG = {
  endpointBase: '/journals',
  idField: 'id',
  numberKey: 'journal_no',
  entityLabel: 'القيد المحاسبي',
  actionAmend: 'journal.amend',
  actionAmendPost: 'journal.amend.post',
  testIdPrefix: 'jrn',
  accentClass: 'bg-indigo-600 hover:bg-indigo-700',
  amendableFields: [
    { key: 'description',  label: 'الوصف',        type: 'textarea' },
    { key: 'reference',    label: 'مرجع',         type: 'text' },
    { key: 'journal_date', label: 'تاريخ القيد',  type: 'text' },
  ],
  financialFields: new Set([
    'total_debit', 'total_credit',
  ]),
  headlineFieldOrder: ['total_debit', 'total_credit'],
  // Mirror server's UNBALANCED_JOURNAL guard client-side so the
  // operator gets immediate feedback on line edits if/when the lines
  // editor is added. Reads the merged snapshot (original + patch) and
  // checks ∑debit == ∑credit.
  validate: (snapshot /*, original, patch */) => {
    const { debit, credit } = sumLines(snapshot?.lines);
    if (Math.abs(debit - credit) > 0.01) {
      return `القيد غير متوازن — المدين ${debit} ≠ الدائن ${credit}`;
    }
    return null;
  },
};

export default function JournalVersionsPanel({ journal, onUpdated }) {
  return (
    <VersioningPanel
      entity={journal}
      config={JOURNAL_CONFIG}
      onUpdated={(data) => onUpdated && onUpdated(data?.journal || data)}
    />
  );
}
